import {Injectable} from "@angular/core";
import gql from "graphql-tag";
import {Observable} from "rxjs/Observable";
import {Apollo, ApolloQueryObservable} from "apollo-angular";
import {Depot} from "../../../shared/view-model/store-management/depot.type";
import {VisbosService} from "../../../core/visbos-core/graphql/visbos.service";
import {DepotYard} from "../../../shared/view-model/store-management/depot-yard.type";
import {stockFragments} from "../stock.service";
import {NgRedux} from "@angular-redux/store";
import {IAppState} from "../../../shared/redux-store";
import {SessionActions} from "../../../shared/actions";
import {map} from "rxjs/operators";
import {ApolloQueryResult} from "apollo-client";

export const depotYardFragments = {
  depotYard: gql`
    fragment DepotYardFields on DepotYard {
      cuid, label, name, description
    }`
};

export const depotFragments = {
  depot: gql`
    fragment DepotFields on Depot {
      cuid, label, name, description,
      administration_unit {cuid, label},
      centralDepot {cuid, label},
      auto_order_proposals_enabled,
      depot_yards {
        ...DepotYardFields
      }
      managed_by_admin_units {
        cuid label
      }
      identities {cuid created_at label ean ean_type is_seal}
    }

    ${depotYardFragments.depotYard}
  `
};

export const fetchDepotsQuery = gql`
  query FetchDepotsQuery($AU: String $cuid: String){
    fetchDepots(cuid: $cuid, administration_unit_cuid: $AU) {
      ...DepotFields
    }
  }

  ${depotFragments['depot']}
`;

export const fetchDepotDetailsQuery = gql`
  query FetchDepotDetailsQuery($AU: String! $cuid: String){
    fetchDepots(cuid: $cuid, administration_unit_cuid: $AU) {
      ...DepotFields
      stocks {
        ...StockDetailFields
      }
    }
  }
  ${stockFragments.stock}
  ${depotFragments['depot']}
`;

export const fetchDepotYardsQuery = gql`
  query FetchDepotYardsForDepot($depot_id: String) {
    depotYards(depot_cuid: $depot_id) {
      ...DepotYardFields
    }
  }

  ${depotYardFragments.depotYard}
`;

export interface FetchDepotsResult {
  fetchDepots: Depot[]
}

@Injectable()
export class DepotService extends VisbosService {


  constructor(apollo: Apollo, ngRedux: NgRedux<IAppState>, sessionActions: SessionActions) {
    super(apollo, ngRedux, sessionActions);
  }

  public getDepotsList(administration_unit_cuid?: string | Observable<string>): ApolloQueryObservable<Depot[]> {

    let data$ = this.fetchItems({
      query: fetchDepotsQuery,
      variables: {
        AU: administration_unit_cuid || this.activeAdminUnitCuid$
      }
    });

    let model$ = data$.pipe(map(({data}: any) => data.fetchDepots));

    return <ApolloQueryObservable<Depot[]>>model$;
  }

  public getDepot(depotCuid: string | Observable<string>, administration_unit_cuid?: string | Observable<string>): ApolloQueryObservable<FetchDepotsResult> {
    return this.fetchItems({
      query: fetchDepotsQuery,
      variables: {
        AU: administration_unit_cuid || this.activeAdminUnitCuid$,
        cuid: depotCuid
      }
    });
  }

  public getDepotWithStocks(depotCuid: string | Observable<string>, administration_unit_cuid?: string | Observable<string>): ApolloQueryObservable<FetchDepotsResult> {
    return this.fetchItems({
      query: fetchDepotDetailsQuery,
      variables: {
        AU: administration_unit_cuid || this.activeAdminUnitCuid$,
        cuid: depotCuid
      }
    });
  }

  public getDepotsWithStocks(onlyCriticalStocks: boolean = false, administrationUnitCuid?: string | Observable<string>): ApolloQueryObservable<Depot[]> {

    let data$ = this.fetchItems({
      query: gql`
        query DepotsWithCriticalStocks($adminUnitCuid: String!, $onlyCriticalStocks: Boolean = false) {
          fetchDepots(administration_unit_cuid: $adminUnitCuid) {
            ...DepotFields
            stocks(only_critical: $onlyCriticalStocks) {
              cuid, label, act_stock, shall_stock, report_stock, has_open_orders
            }
          }
        }

        ${depotFragments['depot']}
      `,
      variables: {
        adminUnitCuid: administrationUnitCuid || this.activeAdminUnitCuid$,
        onlyCriticalStocks: onlyCriticalStocks
      }
    });

    return <ApolloQueryObservable<Depot[]>>data$.pipe(map(({data}: any) => data.fetchDepots));
  }

  public addDepot(depotData): Observable<Depot> {
    return this.mutate({
      mutation: gql`
        mutation AddDepotMutation($depot_input: DepotInput!) {
          addDepot(depot: $depot_input) {
            ...DepotFields
          }
        }

        ${depotFragments.depot}
      `,
      variables: {
        depot_input: depotData
      },
      refetchQueries: ['FetchDepotsQuery']
    }).pipe(
      map((res: any) => <DepotYard>res.addDepot)
    );
  }

  public editDepot(depotCuid: string, depotData): Observable<Depot> {
    return this.mutate({
      mutation: gql`
        mutation UpdateDepotMutation($depot_input: DepotInput!) {
          updateDepot(depot: $depot_input) {
            ...DepotFields
          }
        }

        ${depotFragments.depot}
      `,
      variables: {
        depot_input: depotCuid ? Object.assign(depotData, {cuid: depotCuid}) : depotData
      }
    }).pipe(
      map((res: any) => <DepotYard>res.updateDepot)
    );
  }

  public deleteDepot(depotCuid: string) {
    return this.mutate({
      mutation: gql`
        mutation DeleteDepotMutation($cuid: String!) {
          deleteDepot(cuid: $cuid)
        }`,
      variables: {
        cuid: depotCuid
      },
      updateQueries: this.deleteItemsFromQueries(depotCuid, 'fetchDepots', 'FetchDepotsQuery'),
      refetchQueries: ['FetchDepotsQuery']
    });
  }


  public getDepotYards(depot_cuid: string | Observable<string>): ApolloQueryObservable<DepotYard[]> {

    let data$ = this.fetchItems<DepotYard[]>({
      query: fetchDepotYardsQuery,
      variables: {
        depot_id: depot_cuid
      }
    });

    let model$ = data$.pipe(
      map(({data}: any) => data.depotYards)
    );

    return <ApolloQueryObservable<DepotYard[]>>model$;
  }


  public addDepotYard(depotYardData, depotCuid?: string): Observable<DepotYard> {
    return this.mutate({
      mutation: gql`
        mutation AddDepotYardMutation($depot_yard_input: DepotYardInput!) {
          addDepotYard(depot_yard: $depot_yard_input) {
            ...DepotYardFields
          }
        }

        ${depotYardFragments.depotYard}
      `,
      variables: {
        depot_yard_input: depotCuid ? Object.assign(depotYardData, {depot_id: depotCuid}) : depotYardData
      },
      refetchQueries: ['FetchDepotYardsForDepot']
    }).pipe(
      map((res: any) => <DepotYard>res.addDepotYard)
    );
  }

  public editDepotYard(depotYardCuid: string, depotYardData): Observable<ApolloQueryResult<DepotYard>> {
    return this.mutate({
      mutation: gql`
        mutation UpdateDepotYardMutation($depot_yard_input: DepotYardInput!) {
          updateDepotYard(depot_yard: $depot_yard_input) {
            ...DepotYardFields
          }
        }

        ${depotYardFragments.depotYard}
      `,
      variables: {
        depot_yard_input: depotYardCuid ? Object.assign(depotYardData, {cuid: depotYardCuid}) : depotYardData
      }
    });
  }

  public deleteDepotYard(depotYardCuid: string) {
    return this.mutate({
      mutation: gql`
        mutation DeleteDepotYardMutation($cuid: String!) {
          deleteDepotYard(cuid: $cuid)
        }`,
      variables: {
        cuid: depotYardCuid
      },
      refetchQueries: ['FetchDepotYardsForDepot']
    });
  }

}



// WEBPACK FOOTER //
// ./src/app/store-management/services/depot-service/depot.service.ts